import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { useEnterpriseStore } from "@/store/enterpriseStore";
import { format } from "date-fns";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Edit2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useUserStore } from "@/store/userStore";

interface PreviousProject {
  projectName: string;
  description: string;
  budget: number;
  completedOn: string;
  _id: string;
}

interface EnterpriseProfileProjectsProps {
  formData: {
    previousProjects: PreviousProject[];
    [key: string]: any;
  };
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  updateEnterpriseProfile: (
    id: string,
    data: Partial<any>,
    token: string
  ) => Promise<void>;
  handleCancel?: () => void;
}

export const EnterpriseProfileProjects = ({
  formData,
  handleInputChange,
  setFormData,
  updateEnterpriseProfile,
  handleCancel
}: EnterpriseProfileProjectsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const { user } = useUserStore();
  const { profile } = useEnterpriseStore();
  const projects = profile?.previousProjects || [];

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!user?.accessToken || !user?.userId) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      return;
    }

    if (
      !formData.previousProjects[0].projectName
    ) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const updatedData = {
      ...formData,
      previosProjects: formData.previousProjects,
    };

    try {
      await updateEnterpriseProfile(
        user?.userId,
        updatedData,
        user?.accessToken
      );
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your enterprise profile has been successfully updated.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAddProject = () => {
    setFormData((prev: any) => ({
      ...prev,
      previousProjects: [
        ...prev.previousProjects,
        {
          projectName: "",
          description: "",
          budget: 0,
          completedOn: new Date().toISOString().split("T")[0],
          _id: `temp-${Date.now()}`,
        },
      ],
    }));
  };

  const handleRemoveProject = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      previousProjects: prev.previousProjects.filter(
        (_: any, i: number) => i !== index
      ),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Work History</h2>
      </div>
      <Card className="hover:shadow-md transition-all duration-300 border-blue-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl">
            Work History{" "}
            {projects.length !== 0 && !isEditing && (
              <Button variant="ghost" size="sm" onClick={handleEditClick}>
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <div>
          {projects.length === 0 && !isEditing ? (
            <p className="text-gray-600 mx-4 my-4">No Work History found.</p>
          ) : (
            <>
              {(isEditing ? formData.previousProjects : projects).map(
                (project: PreviousProject, index: number) => (
                  <div
                    key={project._id}
                    className="relative pb-8 animate-fade-in mx-4"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className={`ml-2 p-4 rounded-lg ${
                        !isEditing && "bg-emerald-50 border-emerald-100"
                      } shadow-sm hover:shadow transition-all`}
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-2 mb-2">
                          <Input
                            name={`previousProjects[${index}].projectName`}
                            value={project.projectName}
                            onChange={handleInputChange}
                            className="w-full font-medium"
                            placeholder="Project name"
                            required
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveProject(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ) : (
                        <p className="text-gray-800 font-medium">
                          {project.projectName || "Untitled Project"}
                        </p>
                      )}
                      <div className="flex items-left flex-col gap-1 mt-2 text-xs">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <div className="w-1/3">Description</div>
                            <Input
                              name={`previousProjects[${index}].description`}
                              value={project.description}
                              onChange={handleInputChange}
                              className="w-full"
                              placeholder="Project description"
                              required
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-gray-600 font-medium">
                              Description: {project.description || "-"}
                            </p>
                          </div>
                        )}

                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <div className="w-1/3">Budget</div>
                            <Input
                              type="number"
                              name={`previousProjects[${index}].budget`}
                              value={project.budget}
                              onChange={handleInputChange}
                              className="w-full"
                              placeholder="Project budget"
                              required
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-gray-600 font-medium">
                              Budget: ${project.budget || "0"}
                            </p>
                          </div>
                        )}

                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <div className="w-1/3">Completed on</div>
                            <Input
                              type="date"
                              name={`previousProjects[${index}].completedOn`}
                              value={project.completedOn?.split("T")[0]}
                              onChange={handleInputChange}
                              className="w-full"
                              placeholder="Completed on"
                              required
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-gray-600 font-medium">
                              Completed On:{" "}
                              {project?.completedOn ? format(new Date(project?.completedOn), "PPP"):"-"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}

              {isEditing && (
                <div className="m-2">
                  <Button
                    className="bg-blue-500 text-white"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                  <Button
                    className="bg-blue-500 text-white mx-2"
                    onClick={()=>{
                      handleEditClick()
                      handleCancel()
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
